import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AGENDA_ITEM_BODY_MAX, AGENDA_TITLE_MAX, type AgendaKind } from "@msc/shared";

import { useAgenda } from "../../hooks/usePresentation.js";
import { renderAgendaMarkdown } from "../../lib/customerView/agendaMarkdown.js";
import { shouldAdoptAgenda } from "../../lib/customerView/agendaPrep.js";
import { agendaTitleFallback, agendaTitleOf } from "../../lib/customerView/agendaTitle.js";
import { describeApiError } from "../../lib/errorText.js";
import { usePresentationStore } from "../../stores/presentation.js";
import { AgendaTemplatesPanel } from "../settings/AgendaTemplatesPanel.js";

/** Gleiche Obergrenzen wie `agendaItemSchema` in `@msc/shared`. */
const TITLE_MAX = AGENDA_TITLE_MAX;
const BODY_MAX = AGENDA_ITEM_BODY_MAX;

export interface AgendaPrepProps {
  customerObjectKey: string;
  customerName: string;
}

/**
 * Die Agenda-Punkte in der Vorbereitung (D9): an/aus, Titel, Markdown-Text
 * und eine Vorschau dessen, was der Kunde später auf der Bühne sieht.
 *
 * Die Vorlagen kommen fertig gerendert aus der API (Platzhalter sind dort
 * schon durch CRM-Zahlen ersetzt). Übernommen werden sie nur EINMAL — sobald
 * der Lead etwas geändert hat, gewinnt seine Fassung, bis er „Vorlagen neu
 * laden" drückt (`shouldAdoptAgenda`). Ohne diese Regel verlöre er seine
 * Änderungen bei jedem erneuten Laden der Abfrage.
 *
 * Die Vorschau setzt das Ergebnis von `renderAgendaMarkdown` ein, also bereits
 * durch `sanitizeForDisplay` gelaufenes HTML — die Bühne führt es später ein
 * zweites Mal durch denselben Riegel (Sicherheitsinvariante, Spec D9).
 */
export function AgendaPrep({ customerObjectKey, customerName }: AgendaPrepProps) {
  const { t } = useTranslation();

  const agenda = useAgenda(customerObjectKey, customerName);
  const items = usePresentationStore((s) => s.agendaItems);
  const setAgendaItems = usePresentationStore((s) => s.setAgendaItems);
  const updateAgendaItem = usePresentationStore((s) => s.updateAgendaItem);

  const [previewOpen, setPreviewOpen] = useState<Partial<Record<AgendaKind, boolean>>>({});
  const [reloading, setReloading] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Erstes erfolgreiches Laden: Vorlagen übernehmen. Danach hält
  // `shouldAdoptAgenda` die Hand über die Änderungen des Leads. Der Zustand
  // wird bewusst über `getState()` gelesen und nicht abonniert — sonst liefe
  // der Effekt nach jedem Tastendruck im Textfeld erneut, nur um jedes Mal
  // dasselbe „nein" zu bekommen.
  const loaded = agenda.data;
  useEffect(() => {
    if (!loaded) return;
    const state = usePresentationStore.getState();
    if (
      !shouldAdoptAgenda({
        storeItems: state.agendaItems,
        storeObjectKey: state.agendaObjectKey,
        objectKey: customerObjectKey,
      })
    ) {
      return;
    }
    state.setAgendaItems(loaded.items, customerObjectKey);
  }, [loaded, customerObjectKey]);

  const previews = useMemo(
    () =>
      Object.fromEntries(
        items.filter((a) => previewOpen[a.kind]).map((a) => [a.kind, renderAgendaMarkdown(a.body)]),
      ) as Partial<Record<AgendaKind, string>>,
    [items, previewOpen],
  );

  /**
   * Ausdrücklicher Klick — und nur dann: Die frisch gerenderten Vorlagen
   * ersetzen die Punkte des Termins samt aller Änderungen. Genau deshalb
   * geschieht es nirgends automatisch.
   */
  const reload = async () => {
    setReloading(true);
    try {
      const res = await agenda.refetch();
      // `refetch()` wirft nicht — es meldet. Und es liefert bei einem Fehler
      // die ZULETZT erfolgreichen Daten weiter mit: Auf `res.data` allein zu
      // prüfen hieße, die alten Vorlagen erneut zu übernehmen und dazu
      // „neu geladen" zu melden. Beides ist falsch, das Zweite schlimmer.
      if (!res.isError && res.data) {
        setAgendaItems(res.data.items, customerObjectKey);
        notifications.show({ color: "green", message: t("pages.customerView.present.agendaReloaded") });
      } else {
        notifications.show({ color: "red", message: t("pages.customerView.present.agendaFailed") });
      }
    } finally {
      setReloading(false);
    }
  };

  const enabledCount = items.filter((a) => a.enabled).length;

  return (
    <Stack gap="sm">
      <Group justify="space-between" gap="sm" wrap="wrap">
        <Text size="sm" c="dimmed">
          {t("pages.customerView.present.agendaEnabledCount", { n: enabledCount, total: items.length })}
        </Text>
        <Group gap="xs">
          <Button size="compact-xs" variant="subtle" onClick={() => setTemplatesOpen(true)}>
            {t("pages.customerView.present.agendaTemplatesButton")}
          </Button>
          <Button
            size="compact-xs"
            variant="default"
            loading={reloading}
            disabled={agenda.isLoading}
            onClick={() => void reload()}
          >
            {t("pages.customerView.present.agendaReload")}
          </Button>
        </Group>
      </Group>

      {agenda.isLoading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">{t("pages.customerView.present.agendaLoading")}</Text>
        </Group>
      )}

      {agenda.isError && (
        <Alert color="red" variant="light">{describeApiError(agenda.error, t)}</Alert>
      )}

      {agenda.data?.reason === "unlinked" && (
        <Alert color="yellow" variant="light">{t("pages.customerView.present.agendaUnlinked")}</Alert>
      )}
      {agenda.data?.reason === "out_of_reach" && (
        <Alert color="yellow" variant="light">{t("pages.customerView.present.agendaOutOfReach")}</Alert>
      )}

      {!agenda.isLoading && items.length === 0 && (
        <Text size="sm" c="dimmed">{t("pages.customerView.present.agendaEmpty")}</Text>
      )}

      {items.map((a) => {
        const preview = previews[a.kind];
        return (
          <Paper key={a.kind} withBorder p="xs">
            <Stack gap={6}>
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <Checkbox
                  mt={6}
                  checked={a.enabled}
                  aria-label={t("pages.customerView.present.agendaEnabled", { title: agendaTitleOf(a, t) })}
                  onChange={(e) => updateAgendaItem(a.kind, { enabled: e.currentTarget.checked })}
                />
                <TextInput
                  style={{ flex: 1, minWidth: 0 }}
                  maxLength={TITLE_MAX}
                  value={a.title}
                  aria-label={t("pages.customerView.present.agendaTitle")}
                  placeholder={t("pages.customerView.present.agendaTitle")}
                  onChange={(e) => updateAgendaItem(a.kind, { title: e.currentTarget.value })}
                  // Beim Tippen darf das Feld leer sein — beim Verlassen kehrt
                  // der Vorgabetitel zurück. Ein leerer Titel stünde sonst als
                  // Überschrift ohne Namen auf der Bühne, und
                  // `agendaItemSchema` lehnte ihn beim Protokoll ab.
                  onBlur={() => {
                    if (a.title.trim() === "") {
                      updateAgendaItem(a.kind, { title: agendaTitleFallback(a.kind, t) });
                    }
                  }}
                />
                <Button
                  size="compact-xs"
                  variant={preview === undefined ? "default" : "light"}
                  onClick={() => setPreviewOpen((p) => ({ ...p, [a.kind]: !p[a.kind] }))}
                >
                  {preview === undefined
                    ? t("pages.customerView.present.agendaPreviewShow")
                    : t("pages.customerView.present.agendaPreviewHide")}
                </Button>
              </Group>
              <Textarea
                autosize
                minRows={4}
                maxRows={16}
                maxLength={BODY_MAX}
                value={a.body}
                aria-label={t("pages.customerView.present.agendaBody")}
                styles={{ input: { fontFamily: "monospace", fontSize: "0.8rem" } }}
                onChange={(e) => updateAgendaItem(a.kind, { body: e.currentTarget.value })}
              />
              <Text size="xs" c="dimmed">
                {t("pages.customerView.present.characters", { n: a.body.length, max: BODY_MAX })}
              </Text>
              {preview !== undefined && (
                <Paper withBorder p="xs" bg="var(--msc-flaeche)">
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                    {t("pages.customerView.present.agendaPreview")}
                  </Text>
                  {/* Bereits sanitisiert (`renderAgendaMarkdown`). */}
                  <div className="agenda-preview" dangerouslySetInnerHTML={{ __html: preview }} />
                </Paper>
              )}
            </Stack>
          </Paper>
        );
      })}

      {/*
        Vorlagenpflege (D9: „alle Leads") lebt bewusst hier statt unter
        `/settings` — die Route dort verlangt Admin/BL/Stellv.
        (`AgendaTemplatesPanel.tsx` begründet das ausführlich). Ein Modal
        statt einer eigenen Seite, weil die Vorlagen keinen Kundenbezug
        haben — sie ändern nichts an DIESEM Termin, nur an dem, was der
        nächste „Vorlagen neu laden"-Klick hier oder anderswo bekommt.
      */}
      <Modal
        opened={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title={t("pages.customerView.present.agendaTemplatesModalTitle")}
        size="xl"
        centered
      >
        <AgendaTemplatesPanel />
      </Modal>
    </Stack>
  );
}
